import { PRODUCT_NAME } from "@mailhand/core/brand";
import type { ActionCreate, ActionMode, ActionSpec } from "@mailhand/core/schemas";
import { type SubmitEvent, type ReactNode, useState } from "react";
import { ACTION_MODE_LABEL, ACTION_TYPE_LABEL } from "../../app/labels.js";
import { Button, Card, CardHeader, Field, Input, Segmented, Select, Switch, Textarea } from "../../ui/index.js";
import {
  type ActionDraft,
  type FieldErrors,
  SECRET_MIN_LENGTH,
  blankInput,
  summaryInput,
  validateForCreate,
  validateForEdit,
} from "./draft.js";
import { InputsEditor } from "./InputsEditor.js";

export type ActionFormSubmit = { intent: "create"; body: ActionCreate } | { intent: "edit"; spec: ActionSpec };

interface ActionFormProps {
  initial: ActionDraft;
  intent: "create" | "edit";
  busy: boolean;
  onSubmit: (submit: ActionFormSubmit) => void;
  onCancel: () => void;
  /** Buttons shown beside Save, such as Test and Delete. */
  extra?: ReactNode;
}

const MODE_OPTIONS = [
  { value: "approve", label: ACTION_MODE_LABEL.approve },
  { value: "auto", label: ACTION_MODE_LABEL.auto },
] as const satisfies readonly { value: ActionMode; label: string }[];

export const DESCRIPTION_HINT = "Describe the action well so Hooky has context on how and when to use it. Say when to use it and when not to.";
export const FOLLOW_UP_HINT = "After this action, the email stays on your follow-up list until someone closes it.";

function replyWithOptions(draft: ActionDraft): { value: string; label: string }[] {
  const fromInputs = draft.inputs
    .filter((input) => input.name.trim() !== "" && (input.type === "text" || input.type === "string"))
    .map((input) => ({ value: `input:${input.name.trim()}`, label: `The input "${input.name.trim()}"` }));
  return [{ value: "none", label: "Nothing" }, { value: "response", label: "The webhook's reply" }, ...fromInputs];
}

export function ActionForm({ initial, intent, busy, onSubmit, onCancel, extra }: ActionFormProps) {
  const [draft, setDraft] = useState<ActionDraft>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const set = (change: Partial<ActionDraft>): void => { setDraft((current) => ({ ...current, ...change })); };
  const hasSummary = draft.inputs.some((input) => input.name === "summary");

  function submit(event: SubmitEvent): void {
    event.preventDefault();
    if (intent === "create") {
      const result = validateForCreate(draft);
      setErrors(result.ok ? {} : result.errors);
      if (result.ok) onSubmit({ intent, body: result.body });
      return;
    }
    const result = validateForEdit(draft);
    setErrors(result.ok ? {} : result.errors);
    if (result.ok) onSubmit({ intent, spec: result.body });
  }

  const errorCount = Object.keys(errors).length;

  return (
    <form className="stack-lg" onSubmit={submit} noValidate aria-label={`${ACTION_TYPE_LABEL[draft.type]} action`}>
      <Card>
        <CardHeader eyebrow={`Action Type: ${ACTION_TYPE_LABEL[draft.type]}`} />
        <div className="form-grid">
          <Field label="Name" error={errors.name}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} value={draft.name} maxLength={40} placeholder="Refund" aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { set({ name: event.target.value }); }} />
            )}
          </Field>
          <Field label="Daily limit" hint="Runs per day before email is held." error={errors.dailyCap}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} className="mono" type="number" inputMode="numeric" min={1} max={10000} value={draft.dailyCap} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { set({ dailyCap: event.target.value }); }} />
            )}
          </Field>
        </div>
        <Field label="Description" hint={DESCRIPTION_HINT} error={errors.description}>
          {({ id, describedBy, invalid }) => (
            <Textarea id={id} rows={3} value={draft.description} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { set({ description: event.target.value }); }} />
          )}
        </Field>
        <div className="form-grid form-grid-center">
          <div className="field">
            <span className="field-label">Mode</span>
            <Segmented label="Mode" value={draft.mode} options={MODE_OPTIONS} onChange={(mode) => { set({ mode }); }} />
          </div>
          <Switch checked={draft.enabled} onChange={(enabled) => { set({ enabled }); }} label="Enabled" description={`${PRODUCT_NAME} can pick it.`} />
        </div>
        <Switch checked={draft.followUp} onChange={(followUp) => { set({ followUp }); }} label="Track as a follow-up" description={FOLLOW_UP_HINT} />
      </Card>

      {draft.type === "webhook" ? <WebhookFields draft={draft} errors={errors} intent={intent} set={set} /> : null}

      <Card>
        <CardHeader
          eyebrow="Inputs"
          aside={
            draft.type === "forward" && !hasSummary ? (
              <Button size="sm" variant="ghost" onClick={() => { set({ inputs: [...draft.inputs, summaryInput()] }); }}>
                Add a summary line
              </Button>
            ) : undefined
          }
        />
        <InputsEditor inputs={draft.inputs} errors={errors} onChange={(inputs) => { set({ inputs }); }} onAdd={draft.type === "webhook" ? () => { set({ inputs: [...draft.inputs, blankInput()] }); } : null} />
      </Card>

      <div className="form-foot">
        {errorCount > 0 ? (
          <p className="field-error" role="alert">
            {errors.form ?? "Fix the highlighted fields."}
          </p>
        ) : null}
        <div className="button-row">
          <Button type="submit" variant="primary" busy={busy}>
            {intent === "create" ? "Create action" : "Save"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          {extra}
        </div>
      </div>
    </form>
  );
}

interface WebhookFieldsProps {
  draft: ActionDraft;
  errors: FieldErrors;
  intent: "create" | "edit";
  set: (change: Partial<ActionDraft>) => void;
}

function WebhookFields({ draft, errors, intent, set }: WebhookFieldsProps) {
  return (
    <Card>
      <CardHeader eyebrow="Webhook" />
      <Field label="URL" error={errors["webhook.url"]}>
        {({ id, describedBy, invalid }) => (
          <Input id={id} className="mono" type="url" inputMode="url" placeholder="https://api.yourbusiness.com/hooks/refund" value={draft.webhookUrl} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { set({ webhookUrl: event.target.value }); }} />
        )}
      </Field>
      <div className="form-grid">
        {intent === "create" ? (
          <Field label="Secret" hint={`At least ${SECRET_MIN_LENGTH} characters. Stored encrypted and never shown again.`} error={errors.webhookSecret}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} className="mono" type="password" autoComplete="new-password" value={draft.webhookSecret} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { set({ webhookSecret: event.target.value }); }} />
            )}
          </Field>
        ) : null}
        <Field label="Timeout (seconds)" error={errors["webhook.timeoutMs"]}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} className="mono" type="number" min={1} max={25} value={draft.timeoutSeconds} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { set({ timeoutSeconds: event.target.value }); }} />
          )}
        </Field>
      </div>
      <Field label="After it runs, reply with" error={errors["afterRun.replyWith"]}>
        {({ id, describedBy, invalid }) => (
          <Select id={id} value={draft.replyWith} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { set({ replyWith: event.target.value }); }}>
            {replyWithOptions(draft).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </Card>
  );
}
